import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from '../../../app/hooks';
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
  Container,
  Grid,
  Card,
  CardContent,
  CardHeader,
  LinearProgress,
  Modal,
  Avatar,
  Alert,
} from '@mui/material';
import SportsFootball from '@mui/icons-material/SportsFootball';
import Sports from '@mui/icons-material/Sports';
import { useLoadLeagueData } from '../../../app/dataLoaderHooks';
import { leagueSeasonApi } from '../../../adapters/APIExporter';
import AllTeams from '../team/AllTeams';
import EventSection from './EventSection';
import RecentActivities from './RecentActivities';
import { LeaguePhase, Team, PublicUserInfo } from '../../../sdk/model';
import UserInfoCard from '../../cards/userCard'
import { useValidateAction } from '../../util/ActionValidationUtil';

const getOwnerDisplayName = (owner: PublicUserInfo): string => {
  if (!owner) return 'Owner';
  // Use type assertion to access potential properties
  const ownerAny = owner as any;
  return ownerAny.displayName || ownerAny.username || ownerAny.email || 'Owner';
};

const getOwnerPicture = (owner: PublicUserInfo): string | undefined => {
  if (!owner) return undefined;
  // Use type assertion to access potential picture property
  return (owner as any).picture || undefined;
};

const LeagueHome: React.FC = () => {
  const { leagueId } = useParams<{ leagueId: string }>();
  const [isException, setIsException] = React.useState(false);
  const [isLoadComplete, setLoadComplete] = React.useState(false);
  const [viewingUser, setViewingUser] = React.useState<PublicUserInfo | undefined>(undefined);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const user = useAppSelector((state) => state.user);
  const globalConfig = useAppSelector((state) => state.globalConfigDataLoad);
  const playerDataLoad = useAppSelector((state) => state.playerDataLoad);

  const navigate = useNavigate();
  
  const league = useLoadLeagueData({
    leagueId,
    userId: user.id,
    loadBasicDraftData: false,
    loadFullDraftData: false,
    loadContractData: false,
    loadBasicAuctionData: false,
    loadFullAuctionData: false,
    loadRosterData: true,
    loadRosterFor: [],
    loadWaivedContracts: false,
    loadScheduleData: true,
    isException,
    setIsException,
    isLoadComplete,
    setLoadComplete,
  });
  
  const tradesAllowed = useValidateAction("trade", league)

  const rolloverSeason = React.useCallback(() => {
    if (league?.id) {
      leagueSeasonApi.rolloverSeason(league.id);
    }
  }, [league?.id]);

  const getInviteLink = React.useCallback(() => {
    return `${window.location.host}/league/${league?.id}`;
  }, [league?.id]);

  const copyInviteLink = React.useCallback(() => {
    navigator.clipboard.writeText(getInviteLink());
  }, [getInviteLink]);

  const playerMap = React.useMemo(
    () => new Map(playerDataLoad.flatMap((team) => team.players).map((player) => [player.id, player])),
    [playerDataLoad]
  );

  const teamMap = React.useMemo<Map<string | undefined, Team>>(
    () => new Map(league?.teams?.map((team) => [team.id, team])),
    [league?.teams]
  );

  const currentPlayoffRound = React.useMemo(() => {
    if (!league?.currentSeason) return 0;
    
    let weekTracker = league.currentSeason.finalWeekOfRegularSeason!;
    let playoffRound = 0;
    
    for (let i = 0; weekTracker < league.currentSeason.currentWeek! && i < (league.currentSeason.playoffRoundConfig?.length || 0); i++) {
      const config = league.currentSeason.playoffRoundConfig?.[i];
      if (config) {
        playoffRound = config.roundNumber || 0;
        weekTracker += config.weeks || 0;
      }
    }
    
    return playoffRound;
  }, [league?.currentSeason]);

  const isCommissioner = React.useMemo(
    () => league?.commissioners?.some((commish) => commish.id === user.id) || false,
    [league?.commissioners, user.id]
  );

  const hasEmptyTeams = React.useMemo(
    () => league?.teams?.some((team) => !team?.owners?.length) || false,
    [league?.teams]
  );

  if (!league) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography>Loading league data...</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Modal open={!!viewingUser} onClose={() => setViewingUser(undefined)} 
          aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description"
          sx={{margin: '5%'}}>
        <UserInfoCard userId={viewingUser?.id!} callback={() => setViewingUser(undefined)} />
      </Modal>
      <Card sx={{ mb: 4 }}>
        <CardHeader
          title="League Information"
          sx={{ pb: 0, '& .MuiCardHeader-content': { mb: -1 } }}
        />
        <CardContent sx={{ pt: 1 }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Commissioner{league.commissioners && league.commissioners.length > 1 ? 's' : ''}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
              {league.commissioners?.map(commissioner => 
                <Button 
                  key={commissioner.id} 
                  onClick={() => setViewingUser(commissioner)}
                  variant="outlined"
                  size="small"
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Typography variant="body2">
                    {getOwnerDisplayName(commissioner)}
                  </Typography>
                  {getOwnerPicture(commissioner) && (
                    <Avatar 
                      src={getOwnerPicture(commissioner)} 
                      sx={{ width: 20, height: 20 }}
                    />
                  )}
                </Button>
              )}
            </Box>
          </Box>
          {league.phase === LeaguePhase.RegularSeason && (() => {
            const totalRegularSeasonWeeks = league.currentSeason?.finalWeekOfRegularSeason || 14;
            const currentWeek = league.currentSeason?.currentWeek || 1;
            const completedWeeks = Math.max(0, currentWeek - 1);
            
            // Get current day of the week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
            const currentDayOfWeek = new Date().getDay();
            
            // Thursday-Monday: 4,5,6,0,1 - Show matchup info
            // Tuesday-Wednesday: 2,3 - Show Trade Builder/Waivers
            const isMatchupDays = [0, 1, 4, 5, 6].includes(currentDayOfWeek);
            const isWaiverDays = [2, 3].includes(currentDayOfWeek);
            
            return (
              <Box sx={{ 
                background: theme => `linear-gradient(135deg, ${theme.palette.primary.light}20, ${theme.palette.primary.dark}20)`,
                p: 3,
                borderRadius: 2,
                borderLeft: theme => `4px solid ${theme.palette.primary.main}`,
                mb: 2,
                textAlign: 'center'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
                  <SportsFootball color="primary" fontSize="large" />
                  <Typography variant="h5" component="div" fontWeight="bold">
                    Regular Season • Week {currentWeek}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center', mb: 3 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary" display="block">Season Progress</Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={Math.min(100, (completedWeeks / totalRegularSeasonWeeks) * 100)} 
                      sx={{ height: 8, borderRadius: 2, mt: 0.5, minWidth: 120 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {completedWeeks} of {totalRegularSeasonWeeks} weeks completed
                    </Typography>
                  </Box>
                </Box>

                {isMatchupDays && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 'medium', color: 'primary.main' }}>
                      🏈 It's Football Time! Check out the action and see how you stack up! 🏈
                    </Typography>
                  </Box>
                )}
                {isWaiverDays && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 'medium', color: 'secondary.main' }}>
                      💼 Time to make moves! Build your dynasty through smart trades and waiver pickups! 🚀
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                  {isMatchupDays && (
                    <>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Sports />}
                        onClick={() => navigate(`/league/${league.id}/matchup`)}
                        size="medium"
                        sx={{ px: 3 }}
                      >
                        View Current Matchups
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => navigate(`/league/${league.id}/powerRankings`)}
                        size="medium"
                      >
                        League Standings
                      </Button>
                    </>
                  )}
                  {isWaiverDays && (
                    <>
                      {tradesAllowed && <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => navigate(`/league/${league.id}/trade`)}
                        size="medium"
                        sx={{ px: 3 }}
                      >
                        Trade Builder
                      </Button>}
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => navigate(`/league/${league.id}/players`)}
                        size="medium"
                      >
                        Waiver Wire
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
            );
          })()}
          {league.phase === LeaguePhase.Playoffs && (
            <Box sx={{ textAlign: 'center', p: 2, mb: 2 }}>
              <Typography variant="h5" component="div" gutterBottom fontWeight="bold" color="primary">
                🏆 Playoffs - Round {currentPlayoffRound}
              </Typography>
            </Box>
          )}
          {league.phase === LeaguePhase.SeasonComplete && (
            <Box textAlign="center">
              <Typography variant="h4" component="div" gutterBottom color="primary">
                🏆 {league.currentSeasonYear} Season Complete! 🏆
              </Typography>
              <Typography variant="h5" component="div" gutterBottom>
                Congratulations, <strong>{league.teams?.find((t) => t.currentSeason?.overallRank === 1)?.name}</strong> - League Champions!
              </Typography>
              {isCommissioner && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={rolloverSeason}
                  sx={{ mt: 2 }}
                >
                  Rollover to {league.currentSeasonYear! + 1} Season
                </Button>
              )}
            </Box>
          )}
          {league.phase === LeaguePhase.TrainingCamp && (
            <Box textAlign="center" sx={{ py: 3 }}>
              <Typography variant="h5" component="div" gutterBottom color="primary">
                🏈 Training Camp is in Session! 🏈
              </Typography>
              <Typography variant="body1" paragraph>
                This is your chance to evaluate your roster and make final adjustments before the season begins.
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Check out current contract and roster information for every team in the league during the offseason.
              </Typography>
              
              <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={() => navigate(`/league/${league.id}/draftHistory`)}
                >
                  View Draft Results
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => navigate(`/league/${league.id}/roster-cap`)}
                >
                  Roster &amp; Cap Space
                </Button>
              </Box>
            </Box>
          )}
          {league.phase === LeaguePhase.PreSeason && (
            <Box textAlign="center" sx={{ py: 3 }}>
              <Typography variant="h5" component="div" gutterBottom color="primary">
                🏈 Preseason is Here! 🏈
              </Typography>
              <Typography variant="body1" paragraph>
                The preseason is your opportunity to finalize your roster and scout potential practice squad players.
              </Typography>
              
              <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={() => navigate(`/league/${league.id}/auctionHistory`)}
                >
                  View Auction Results
                </Button>
                <Button 
                  variant="outlined" 
                  color="primary"
                  onClick={() => navigate(`/league/${league.id}/players`)}
                >
                  Check Out Free Agents
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
      
      {league.currentSeason?.tradeDeadlineWeek === league.currentSeason?.currentWeek &&
        <Alert severity="warning"><b>*** This week is the league's trade deadline!  All trading will be disallowed after this week completes. ***</b></Alert>
      }

      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} md={12}>
            <EventSection
              league={league}
              leagueEvent={league?.currentSeason?.draft}
              preparingPhase={LeaguePhase.Offseason}
              eventType="Rookie Draft"
              deadlineEvents="franchise tags and fifth-year options"
              activePhases={[LeaguePhase.DraftPrep, LeaguePhase.Draft]}
              roomUrl="/draft/"
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <EventSection
              league={league}
              leagueEvent={league?.currentSeason?.auction}
              preparingPhase={LeaguePhase.TrainingCamp}
              eventType="Free Agent Auction"
              deadlineEvents="franchise-tagged player long-term extensions"
              activePhases={[LeaguePhase.AuctionPrep, LeaguePhase.Auction]}
              roomUrl="/auction/"
            />
          </Grid>
        </Grid> 
      </Box>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          {hasEmptyTeams && isCommissioner && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
              <Typography variant="body1" gutterBottom>
                There are teams without owners in your league! Share the invite link below:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, alignItems: 'center' }}>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {getInviteLink()}
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small" 
                  onClick={copyInviteLink}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Copy Invite Link
                </Button>
              </Box>
            </Box>
          )}
          <AllTeams league={league} joinEnabled />
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom>
            Recent Activities
          </Typography>
          <RecentActivities 
            league={league}
            playerMap={playerMap}
            teamMap={teamMap}
          />
        </CardContent>
      </Card>
    </Container>
  );
};

export default LeagueHome;